import { z } from 'zod';
import { moneySchema } from '../money';
import { projectAmpelSchema, projectStatusSchema, projectTypeSchema } from './project.schema';

/**
 * Projekt-Kennzahlen über ALLE nicht archivierten Projekte — serverseitig
 * gerechnet (2026-09-29). Vorher zählten Projektgesundheit (Dashboard), das
 * Analyse-Cockpit und die Projekt-Analyse im Browser die erste Listenseite
 * (200 bzw. 500 Projekte); ab dem nächsten Projekt stimmten Ampel, Status-
 * Verteilung und Auftragswerte nicht mehr.
 *
 * „Aktiv/laufend" = Status nicht in `INACTIVE_PROJECT_STATUSES` (wie die
 * Default-Projektliste). Die Ampel ist die automatische Termin-Ampel
 * (`project-health.ts`), dieselbe wie in der Projektliste.
 */

/** Anzahl laufender Projekte je Ampel. */
export const projectAmpelCountsSchema = z.object({
  GRUEN: z.number().int().nonnegative(),
  GELB: z.number().int().nonnegative(),
  ROT: z.number().int().nonnegative(),
});
export type ProjectAmpelCounts = z.infer<typeof projectAmpelCountsSchema>;

/** Länge der „kritische Projekte"-Liste in der Projektgesundheit (Dashboard). */
export const PROJECT_HEALTH_CRITICAL_LIMIT = 6;

/** Ein laufendes Projekt mit Handlungsbedarf (Ampel ROT oder GELB). */
export const projectHealthRowSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  customerName: z.string().nullable(),
  ampel: projectAmpelSchema,
  /**
   * Auftragswert — `null` ohne `projects.viewfinancials`/`projects.viewordervalue`
   * (wie in der Projektliste) oder, im Dashboard, ohne `dashboard.viewfinancials`.
   */
  orderValue: moneySchema.nullable(),
});
export type ProjectHealthRow = z.infer<typeof projectHealthRowSchema>;

/**
 * Projektgesundheit: Ampel-Verteilung über ALLE laufenden Projekte + die
 * dringlichsten (ROT vor GELB; innerhalb gleicher Ampel größerer Auftragswert
 * zuerst, sofern sichtbar; sonst neueste zuerst wie die Projektliste).
 */
export const projectHealthSchema = z.object({
  ampel: projectAmpelCountsSchema,
  critical: z.array(projectHealthRowSchema),
});
export type ProjectHealth = z.infer<typeof projectHealthSchema>;

/**
 * Projekt-Kennzahlen (`GET /v1/projects/summary`, Analyse-Cockpit). Zählungen
 * ohne Finanz-Recht; die Auftragswerte (`orderValue`) nur mit
 * `projects.viewfinancials` oder `projects.viewordervalue` — sonst `null`, nie 0.
 */
export const projectSummarySchema = z.object({
  asOf: z.string().datetime(),
  /** Alle nicht archivierten Projekte. */
  total: z.number().int().nonnegative(),
  /** Davon laufend (nicht geschlossen/storniert). */
  active: z.number().int().nonnegative(),
  /** Anzahl je Status (nur belegte Status, in Status-Reihenfolge). */
  byStatus: z.array(z.object({ status: projectStatusSchema, count: z.number().int().positive() })),
  /** Anzahl je Projektart (nur belegte Arten). */
  byType: z.array(z.object({ projectType: projectTypeSchema, count: z.number().int().positive() })),
  /** Ampel über die laufenden Projekte. */
  ampel: projectAmpelCountsSchema,
  orderValue: z
    .object({
      /** Summe Auftragswert der laufenden Projekte. */
      active: moneySchema,
      /** Summe je Status (alle Projekte mit Auftragswert > 0), absteigend. */
      byStatus: z.array(z.object({ status: projectStatusSchema, value: moneySchema })),
    })
    .nullable(),
});
export type ProjectSummary = z.infer<typeof projectSummarySchema>;
